/**
 * Static configuration for the `machine-overview` widget.
 *
 * The widget shows (top to bottom):
 *   - image (top-level widget.image_url, optional)
 *   - title
 *   - status badge (chosen `status` node value matched against
 *     semantics.status_entries in array order, first match wins)
 *   - "cycles" row: `${good} / ${total}` with label from `row_labels.cycles`
 *   - progress bar: `(good / total) * 100%` (animated)
 *   - "progress" row: `${pct.toFixed(1)}%` with label from `row_labels.progress`
 *   - one row per `encoding.rows[]` entry, each rendering the binding whose
 *     field matches `row.field`
 *
 * No hardcoded list of rows lives here — defaults ship with the BE template
 * as `encoding.rows[]` (deterministic UUID per row), the editor adds rows
 * with random UUIDs, and the renderer iterates whatever is in there. The
 * 3 fixed bindings (`good`/`total`/`status` — drives cycles, progress, and
 * status badge) are listed below so the renderer can recognize them; the
 * user doesn't interact with these via `encoding.rows`.
 *
 * ponytail: pulled out of the widget TSX so future widgets can reuse the
 * status-matching helper (`matchStatusEntry`) and the badge style resolver
 * (`getStatusBadgeStyle`) without importing the component itself.
 */
import type { CSSProperties } from "react";
import type {
  MachineOverviewMetric,
  MachineOverviewMetricCycleUnit,
  MachineOverviewMetricKind,
  MachineOverviewMetricSlot,
  MachineOverviewRow,
  MachineOverviewSemantics,
  MachineOverviewStatusEntry,
  StatusColorToken,
  WidgetPresentation,
} from "@src/types/dashboards";

/** The 3 structural fields the machine-overview widget always needs:
 *  `good`+`total` drive cycles row + progress bar; `status` drives the
 *  status badge. The renderer hardcodes these because they're not
 *  user-editable — only the addresses (via `query.bindings`) and the
 *  label overrides (via `presentation.row_labels`) are user-tunable. */
export const MACHINE_OVERVIEW_FIXED_BINDING_KEYS = [
  "good",
  "total",
  "status",
] as const;

export type MachineOverviewFixedBindingKey =
  (typeof MACHINE_OVERVIEW_FIXED_BINDING_KEYS)[number];

/** How a matched status entry is rendered on the widget root. "badge"
 *  shows a colored chip with the entry's label; "background" paints the
 *  widget's background with the entry's color instead. Both share the
 *  same matching logic (matchStatusEntry) and the same color tokens, so
 *  switching is purely a presentation preference and lives on
 *  `presentation.status_display_mode` — see edit-widget-drawer-modal. */
export type MachineOverviewDisplayMode = "badge" | "background";

/** Resolve the label for a row. Lookup order:
 *  1. `presentation.row_labels[row.id]` — user override keyed by stable UUID
 *  2. `presentation.row_labels[row.field]` — legacy override keyed by the
 *     row's field name. Pre-`encoding.rows` widgets stored overrides here;
 *     keeps them readable on the very first save after the migration.
 *  3. `i18n(row.default_i18n_key)` — translated default for rows shipped
 *     with the widget template. User-added rows don't carry a key.
 *  4. `row.label` — whatever was last edited/seeded, used verbatim. */
export const resolveRowLabel = (
  row: MachineOverviewRow,
  rowLabels: WidgetPresentation["row_labels"] | undefined,
  i18n: (key: string) => string,
): string => {
  const fromUserId = rowLabels?.[row.id];
  if (fromUserId && fromUserId.length > 0) return fromUserId;
  const fromUserField = rowLabels?.[row.field];
  if (fromUserField && fromUserField.length > 0) return fromUserField;
  if (row.default_i18n_key) return i18n(row.default_i18n_key);
  return row.label;
};

/** Status matching. Given the (typed-or-not) last value of the `status`
 *  node and the user-configured `status_entries` array, returns the first
 *  matching entry. `null` when nothing matches OR the node has no value.
 *
 *  Matching rules:
 *    - mode "exact": rawValue must === entry.value (string OR number).
 *      Type-strict equality. Mixed string/number won't match — the user
 *      picks the literal that suits the node's reported type.
 *    - mode "interval": rawValue must be a number and within [min, max]
 *      inclusive on both ends. Non-numeric rawValues fall through. */
export const matchStatusEntry = (
  entries: MachineOverviewStatusEntry[] | undefined,
  rawValue: string | number | null | undefined,
): MachineOverviewStatusEntry | null => {
  if (!entries || entries.length === 0 || rawValue == null) return null;
  for (const entry of entries) {
    if (entry.mode === "exact") {
      if (entry.value === undefined) continue;
      if (entry.value === rawValue) return entry;
    } else if (entry.mode === "interval") {
      if (typeof entry.min !== "number" || typeof entry.max !== "number")
        continue;
      if (typeof rawValue !== "number") continue;
      if (rawValue >= entry.min && rawValue <= entry.max) return entry;
    }
  }
  return null;
};

/** Token → hex, single source of truth. Storage stays on tokens (existing
 *  widgets on the BE keep the token strings); hex is derived at render time
 *  for ColorSwatchPicker and badge style. Mirrors the inline hex strings
 *  in getStatusBadgeStyle so the picker swatch matches the renderer badge
 *  exactly. */
export const STATUS_COLOR_HEX: Record<StatusColorToken, string> = {
  alarmRed: "#fd3838",
  doneGreen: "#19af00",
  laserOrange: "#fd7f38",
  inactiveGrey: "#697477",
  white: "#e6f1f2",
};

/** Reverse lookup for ColorSwatchPicker onChange — turns selected hex back
 *  into the token persisted on the saved widget. */
export const HEX_TO_STATUS_TOKEN: Record<string, StatusColorToken> =
  Object.fromEntries(
    (Object.entries(STATUS_COLOR_HEX) as [StatusColorToken, string][]).map(
      ([k, v]) => [v, k],
    ),
  );

/** Display order for ColorSwatchPicker — alarm → ok → warning → neutral →
 *  foreground. Keeps the picker's read direction matching the badge
 *  severity scale the renderer applies. */
export const STATUS_SWATCHES: { value: string; label: string }[] = [
  { value: STATUS_COLOR_HEX.alarmRed, label: "alarm red" },
  { value: STATUS_COLOR_HEX.doneGreen, label: "done green" },
  { value: STATUS_COLOR_HEX.laserOrange, label: "laser orange" },
  { value: STATUS_COLOR_HEX.inactiveGrey, label: "inactive grey" },
  { value: STATUS_COLOR_HEX.white, label: "white" },
];

/** Resolve the badge style for the status row as inline CSS (backgroundColor
 *  uses a 10-12% alpha faded tint of the active color so the contrast still
 *  reads on a dark canvas). Centralizing this here so the drawer preview and
 *  the renderer agree on colors without duplicating the palette switch. */
export const getStatusBadgeStyle = (
  colorToken: StatusColorToken | undefined,
): CSSProperties => {
  switch (colorToken) {
    case "alarmRed":
      return {
        backgroundColor: "rgba(253, 56, 56, 0.1)",
        borderColor: "#fd3838",
        color: "#fd3838",
        outline: "1px solid #fd3838",
      };
    case "doneGreen":
      return {
        backgroundColor: "rgba(25, 175, 0, 0.1)",
        borderColor: "#19af00",
        color: "#19af00",
        outline: "1px solid #19af00",
      };
    case "laserOrange":
      return {
        backgroundColor: "rgba(253, 127, 56, 0.1)",
        borderColor: "#fd7f38",
        color: "#fd7f38",
        outline: "1px solid #fd7f38",
      };
    case "inactiveGrey":
      return {
        backgroundColor: "rgba(105, 116, 119, 0.4)",
        borderColor: "#697477",
        color: "#697477",
        outline: "1px solid #697477",
      };
    case "white":
      return {
        backgroundColor: "rgba(230, 241, 242, 0.1)",
        borderColor: "#e6f1f2",
        color: "#e6f1f2",
        outline: "1px solid #e6f1f2",
      };
    default:
      return {
        backgroundColor: "transparent",
        borderColor: "transparent",
        color: "transparent",
        outline: "none",
      };
  }
};

/** Type guard that narrows the `WidgetSemantics` union to a machine-overview
 *  semantics payload. The renderer / drawer both call this before reading
 *  `status_entries`. */
export const isMachineOverviewSemantics = (
  s: unknown,
): s is MachineOverviewSemantics =>
  !!s &&
  typeof s === "object" &&
  Array.isArray((s as { status_entries?: unknown }).status_entries);

// --- Metrics ----------------------------------------------------------------

/** Stable field name on `query.bindings[]` for a metric's slot. Encoded
 *  once, reused by editor (seed/snapshot/clear), drawer (save), and
 *  renderer (sliceQueryForField). Changing the prefix is a wire-shape
 *  breaking change — keep it stable. */
export const metricSlotField = (
  metricId: string,
  slot: MachineOverviewMetricSlot,
): string => `metric_${metricId}_${slot}`;

/** Field-name prefix the editor / drawer use to identify which bindings
 *  belong to which metric on a clear-by-id pass. */
export const metricFieldPrefix = (metricId: string): string =>
  `metric_${metricId}_`;

/** All slots a metric kind reads. Used by the editor to render the
 *  required + optional column of dropdowns and by the renderer to know
 *  which hooks to fire in `MetricRow`. */
export const METRIC_SLOTS: Record<
  MachineOverviewMetricKind,
  MachineOverviewMetricSlot[]
> = {
  // Remaining Time = (Cycle × (Total − Good)) / cycle-time-unit-divisor / Cavities.
  // the divisor is now `CYCLE_TIME_UNIT_DIVISOR[metric.cycle_unit]` —
  // 3600 (s), 3.6e6 (ms), or 3.6e9 (μs) — chosen by the user in the
  // editor (no implicit default). Cavities is optional — when the
  // user doesn't pick it the formula omits the divide-by-cavities step.
  remaining_time: ["cycle_time", "total_parts", "good_parts", "cavities"],
  // Quality = Good / (Good + Bad) × 100.
  quality: ["good_parts", "bad_parts"],
};

/** Slots that MUST have a picked node for the metric to be saved. Empty
 *  required slots are a save-blocker (same UX as the existing per-row
 *  + per-line validation). Editor's `validate()` consults this list. */
export const REQUIRED_METRIC_SLOTS: Record<
  MachineOverviewMetricKind,
  MachineOverviewMetricSlot[]
> = {
  remaining_time: ["cycle_time", "total_parts", "good_parts"],
  quality: ["good_parts", "bad_parts"],
};

/** Hex emitted by the editor's `Reset` button (and used as the seed
 *  when a widget has no saved pick). Mirrors the scss `$blue` so the
 *  gradient stays visually identical whether the user explicitly
 *  picked this hex or never opened the editor at all. Also lives in
 *  TREND_LINE_PALETTE as "Blue" — clicking the swatch versus clicking
 *  Reset produce the same wire shape.
 *
 *  Note: legacy widgets saved before this feature shipped have no
 *  `progress_color_start` key. The SCSS gradient's var-fallback
 *  (`var(--mo-progress-fill-start, colors.$blue)`) renders those
 *  identically without a migration. */
export const DEFAULT_PROGRESS_START_HEX = "#3aacc8"; // = scss $blue, = TREND_LINE_PALETTE "Blue"

/** Hex emitted by the editor's `Reset` button (and used as the seed
 *  when a widget has no saved pick). Mirrors the scss `$green40`
 *  (40% alpha blue). Lives in TREND_LINE_PALETTE as "Blue 40%" so
 *  the picker offers it as an explicit click target too. */
export const DEFAULT_PROGRESS_END_HEX = "rgba(58, 172, 200, 0.4)"; // = scss $green40, = TREND_LINE_PALETTE "Blue 40%"

/** Render the metric in insertion order. The renderer iterates this
 *  (instead of just `widget.encoding.metrics`) so a user-added metric
 *  surfaces at the bottom of the widget's metric block even if old
 *  entries carried sparse order numbers. */
export const sortMetricsForDisplay = (
  metrics: MachineOverviewMetric[] | undefined,
): MachineOverviewMetric[] => {
  if (!metrics || metrics.length === 0) return [];
  return metrics.slice().sort((a, b) => a.order - b.order);
};

/** Cycle-time unit → divisor to apply so the formula result lands
 *  in HOURS. SI ladder: `s` = 3 600 s/h, `ms` = 3.6e6 ms/h,
 *  `μs` = 3.6e9 μs/h. Editor gates saves on `cycle_unit` being set
 *  (no default), so this map is always honored when we reach the
 *  formula. The renderer reads `metric.cycle_unit` directly — no
 *  fallback branch needed here because we never get here without one.
 *  Ponytail: integer-power-of-10 ladder, one entry per source unit —
 *  if you ever need a new unit (ns, minutes…) extend the type union
 *  + add the divisor + add i18n keys; do NOT branch inline. */
export const CYCLE_TIME_UNIT_DIVISOR: Record<
  MachineOverviewMetricCycleUnit,
  number
> = {
  seconds: 3_600,
  milliseconds: 3_600_000,
  microseconds: 3_600_000_000,
};

/** Compute Remaining Time from a partial slot map (any slot not fetched
 *  is left undefined / null). Returns null when ANY required slot is
 *  missing, non-finite, or when the result is non-finite (e.g. cavities
 *  turned out to be 0 → ×∞ suppressed into null → UI shows em-dash).
 *  Cavities=null/omitted → formula drops the divide-by-cavities step.
 *  `cycleUnit` selects the divisor for converting the source cycle-time
 *  unit into hours. The editor's validate() guarantees `cycleUnit` is
 *  set for any remaining_time metric that reaches this function call. */
export const computeRemainingTime = (
  slots: Partial<Record<MachineOverviewMetricSlot, number | null | undefined>>,
  cycleUnit: MachineOverviewMetricCycleUnit,
): number | null => {
  const cycle = slots.cycle_time;
  const total = slots.total_parts;
  const good = slots.good_parts;
  const cavities = slots.cavities;
  if (
    cycle == null ||
    total == null ||
    good == null ||
    !Number.isFinite(cycle) ||
    !Number.isFinite(total) ||
    !Number.isFinite(good)
  ) {
    return null;
  }
  const base = (cycle * (total - good)) / CYCLE_TIME_UNIT_DIVISOR[cycleUnit];
  if (cavities == null) return base;
  if (!Number.isFinite(cavities) || cavities === 0) return null;
  return base / cavities;
};

/** Compute Quality (percentage 0..100) from a partial slot map. Returns
 *  null when either required slot is missing, non-finite, or when
 *  good + bad sum to zero (no parts → undefined ratio, em-dash). */
export const computeQuality = (
  slots: Partial<Record<MachineOverviewMetricSlot, number | null | undefined>>,
): number | null => {
  const good = slots.good_parts;
  const bad = slots.bad_parts;
  if (
    good == null ||
    bad == null ||
    !Number.isFinite(good) ||
    !Number.isFinite(bad)
  ) {
    return null;
  }
  const total = good + bad;
  if (total === 0) return null;
  return (good / total) * 100;
};

/** One entry point per kind — the renderer dispatches on `metric.kind`.
 *  Centralizing the dispatch keeps the widget code path simple: one
 *  `computeMetric(metric, slots)` call, no branching at the call site.
 *  Quality is unitless so it ignores `cycle_unit` — no fallthrough
 *  branch needed. */
export const computeMetric = (
  metric: MachineOverviewMetric,
  slots: Partial<Record<MachineOverviewMetricSlot, number | null | undefined>>,
): number | null =>
  metric.kind === "remaining_time"
    ? metric.cycle_unit
      ? computeRemainingTime(slots, metric.cycle_unit)
      : null
    : computeQuality(slots);
